import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Plus, Copy, Check, Trash2, Link as LinkIcon } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { Card, Button, Chip, Field, Input, Select, Skeleton, Modal, Toast } from "@/ui";
import ConfirmAction from "@/components/ConfirmAction";
import { manilaDateTime } from "@/lib/format";

const BASE = process.env.REACT_APP_BACKEND_URL;

const SERVER_LABELS = {
  shopfront: "Shopfront (customer assistant)",
  backoffice: "Back office (owner assistant)",
  platform: "Platform",
};

// Manage the shop's (or platform's) Reto MCP keys. A key is shown ONCE when made
// and is bound to one server and one shop. Both an MCP client and a plain custom
// function use the same key.
export default function McpKeys({ shopId, platform = false }) {
  const servers = platform ? ["platform"] : ["shopfront", "backoffice"];
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [server, setServer] = useState(servers[0]);
  const [label, setLabel] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState("");
  const [revoke, setRevoke] = useState(null);
  const [busy, setBusy] = useState(false);

  const endpointFor = (srv) =>
    srv === "platform" ? `${BASE}/api/mcp/platform` : `${BASE}/api/mcp/${srv}/${shopId}`;

  const load = useCallback(() => {
    setLoading(true);
    const q = platform ? "?server=platform" : `?shopId=${shopId}`;
    api.get(`/mcp/tokens${q}`)
      .then((r) => setTokens(r.data.tokens || []))
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  }, [shopId, platform]);

  useEffect(() => { if (platform || shopId) load(); }, [load, platform, shopId]);

  const issue = async () => {
    setIssuing(true); setError("");
    try {
      const body = platform ? { server: "platform", label } : { shopId, server, label };
      const r = await api.post("/mcp/tokens", body);
      setIssued(r.data);
      setLabel("");
      load();
    } catch (e) { setError(friendlyError(e)); }
    finally { setIssuing(false); }
  };

  const runRevoke = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/mcp/tokens/${revoke.id}/revoke`, platform ? {} : { shopId });
      setToast("That key has been switched off");
      load();
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(false); setRevoke(null); setTimeout(() => setToast(""), 3000); }
  };

  const copy = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag); setTimeout(() => setCopied(""), 1600);
    } catch { setToast("Copy did not work — select the text and copy it by hand"); setTimeout(() => setToast(""), 3000); }
  };

  return (
    <Card data-testid="mcp-keys-card">
      <div className="row" style={{ gap: 10, marginBottom: 8 }}>
        <span className="ph-icon" style={{ margin: 0 }}><KeyRound size={18} strokeWidth={1.75} /></span>
        <h2 className="t-h2">mcp keys</h2>
      </div>
      <p className="muted t-small" style={{ marginBottom: 14, maxWidth: 620 }}>
        Reto makes its own keys for its own servers. A key works one place only — one server, one shop.
        The shopfront server can browse and build a basket; it can never refund, reprice or read your customers.
      </p>

      {error ? <div className="err-text" data-testid="mcp-keys-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        {!platform ? (
          <Field label="server">
            <Select data-testid="mcp-server-select" value={server} onChange={(e) => setServer(e.target.value)}>
              {servers.map((s) => <option key={s} value={s}>{SERVER_LABELS[s]}</option>)}
            </Select>
          </Field>
        ) : null}
        <Field label="name this key">
          <Input data-testid="mcp-key-label" value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Claude on my phone" />
        </Field>
        <Button variant="primary" data-testid="mcp-issue-btn" loading={issuing} onClick={issue}>
          <Plus size={16} strokeWidth={1.75} />make a key
        </Button>
      </div>

      {loading || tokens === null ? (
        <Skeleton variant="card" />
      ) : tokens.length === 0 ? (
        <p className="muted t-small" data-testid="mcp-no-keys">No keys yet. Make one to connect an assistant.</p>
      ) : (
        <div className="tbl-wrap" data-testid="mcp-keys-table">
          <table className="tbl">
            <thead><tr><th>Key</th><th>Server</th><th>Made</th><th>Expires</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} data-testid={`mcp-key-${t.id}`}>
                  <td>{t.label}<span className="mono t-small muted-3" style={{ marginLeft: 6 }}>{t.preview}</span></td>
                  <td className="t-small">{SERVER_LABELS[t.server] || t.server}</td>
                  <td className="muted-3 t-small">{t.createdAt ? manilaDateTime(t.createdAt) : "\u2014"}</td>
                  <td className="muted-3 t-small">{t.expiresAt ? manilaDateTime(t.expiresAt) : "\u2014"}</td>
                  <td><Chip tone={t.revoked ? "bad" : "good"} dot>{t.revoked ? "off" : "live"}</Chip></td>
                  <td>
                    {!t.revoked ? (
                      <Button size="sm" variant="danger" data-testid={`mcp-revoke-${t.id}`} onClick={() => setRevoke(t)}>
                        <Trash2 size={14} strokeWidth={1.75} />switch off
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="divider" style={{ margin: "16px 0" }} />
      <h3 className="t-h3" style={{ marginBottom: 8 }}>server addresses</h3>
      <div className="stack" style={{ gap: 8 }}>
        {servers.map((srv) => (
          <div key={srv} className="row-between" data-testid={`mcp-endpoint-${srv}`}>
            <span className="mono t-small" style={{ wordBreak: "break-all" }}>{endpointFor(srv)}</span>
            <Button size="sm" onClick={() => copy(endpointFor(srv), srv)}>
              {copied === srv ? <><Check size={14} strokeWidth={2} />copied</> : <><LinkIcon size={14} strokeWidth={1.75} />copy</>}
            </Button>
          </div>
        ))}
      </div>

      <Modal open={!!issued} title="Your new key — copy it now"
        onClose={() => setIssued(null)}
        footer={<Button variant="primary" data-testid="mcp-issued-done" onClick={() => setIssued(null)}>Done</Button>}>
        <p className="muted t-small" style={{ marginBottom: 12 }}>
          This is shown only once and cannot be seen again. Keep it secret — never share it.
        </p>
        <div className="tbl-wrap" style={{ padding: 12, position: "relative" }}>
          <Button size="sm" data-testid="mcp-copy-token" onClick={() => copy(issued?.token, "token")}
            style={{ position: "absolute", top: 8, right: 8 }}>
            {copied === "token" ? <><Check size={14} strokeWidth={2} />copied</> : <><Copy size={14} strokeWidth={1.75} />copy</>}
          </Button>
          <pre className="mono t-small" data-testid="mcp-issued-token" style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all", paddingRight: 70 }}>
{issued?.token}
          </pre>
        </div>
      </Modal>

      <ConfirmAction
        open={!!revoke}
        title="Switch off this key?"
        preview={[
          `The key "${revoke?.label || ""}" will stop working straight away.`,
          "Any assistant using it will lose access to this server.",
          "You can always make a new key.",
        ]}
        confirmLabel="Yes, switch it off" cancelLabel="No, keep it" busy={busy} error={error}
        onConfirm={runRevoke} onCancel={() => setRevoke(null)}
      />

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="mcp-keys-toast">{toast}</Toast></div> : null}
    </Card>
  );
}
